import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Close as CloseIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';

import { storeAdTagDomains } from '@/store/adTagDomains';

const SEARCH_DEBOUNCE_MS = 300;

type Props = {
    open: boolean;
    groupName?: string;
    onClose: () => void;
};

/**
 * Экран управления доменами группы (T-12).
 *
 * Список пагинированный и серверный: 100 000 доменов в браузер не грузятся
 * и не рендерятся. Поиск по префиксу — под него на сервере построен индекс.
 */
const DomainManagerDialog = observer(({ open, groupName, onClose }: Props) => {
    const store = storeAdTagDomains;
    const [searchInput, setSearchInput] = useState('');

    // Debounce поиска: не шлём запрос на каждое нажатие
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInput !== store.search) {
                store.setSearch(searchInput);
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [searchInput, store]);

    const handleClose = () => {
        setSearchInput('');
        store.close();
        onClose();
    };

    const handleDeleteSelected = () => {
        void store.removeSelected();
    };

    const hasItems = store.items.length > 0;
    const isFiltered = store.search !== '' || store.activeFilter !== null;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" PaperProps={{ sx: { height: '90vh' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    Управление доменами
                    {groupName ? ` — ${groupName}` : ''}
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        label="Поиск по домену"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        size="small"
                        sx={{ minWidth: 280 }}
                        placeholder="example.com"
                    />

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="adtag-active-filter">Статус</InputLabel>
                        <Select
                            labelId="adtag-active-filter"
                            label="Статус"
                            value={store.activeFilter === null ? 'all' : store.activeFilter ? 'on' : 'off'}
                            onChange={(e) => {
                                const value = e.target.value;
                                store.setActiveFilter(value === 'all' ? null : value === 'on');
                            }}
                        >
                            <MenuItem value="all">Все</MenuItem>
                            <MenuItem value="on">Активные</MenuItem>
                            <MenuItem value="off">Неактивные</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ flexGrow: 1 }} />

                    <Typography variant="body2" color="text.secondary">
                        Всего: {store.meta.total.toLocaleString('ru-RU')}
                    </Typography>

                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        disabled={store.selectedIds.length === 0}
                        onClick={handleDeleteSelected}
                    >
                        Удалить выбранные ({store.selectedIds.length})
                    </Button>
                </Box>

                {store.listError && (
                    <Typography color="error" variant="body2">{store.listError}</Typography>
                )}

                <TableContainer sx={{ flexGrow: 1 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <input
                                        type="checkbox"
                                        aria-label="Выбрать все на странице"
                                        checked={hasItems && store.selectedIds.length === store.items.length}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                store.items.forEach((item) => {
                                                    if (!store.selectedIds.includes(item.id)) {
                                                        store.toggleSelected(item.id);
                                                    }
                                                });
                                            } else {
                                                store.clearSelection();
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>Домен</TableCell>
                                <TableCell width={140}>Статус</TableCell>
                                <TableCell width={160}>Добавлен</TableCell>
                                <TableCell width={130}>Автор</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {store.listLoading && !hasItems && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            )}

                            {store.items.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell padding="checkbox">
                                        <input
                                            type="checkbox"
                                            aria-label={`Выбрать ${item.domain_display || item.domain}`}
                                            checked={store.selectedIds.includes(item.id)}
                                            onChange={() => store.toggleSelected(item.id)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={item.domain_display && item.domain_display !== item.domain
                                            ? `A-label: ${item.domain}`
                                            : ''}
                                        >
                                            <span>{item.domain_display || item.domain}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Переключить статус">
                                            <Chip
                                                label={item.is_active ? 'активен' : 'выключен'}
                                                color={item.is_active ? 'success' : 'default'}
                                                size="small"
                                                variant={item.is_active ? 'filled' : 'outlined'}
                                                onClick={() => void store.toggleActive(item)}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{item.created_at}</TableCell>
                                    <TableCell>{item.created_by ?? '—'}</TableCell>
                                </TableRow>
                            ))}

                            {!store.listLoading && !hasItems && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {isFiltered
                                                ? 'Ничего не найдено по заданным условиям'
                                                : 'В группе пока нет доменов'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center' }}>
                <TablePagination
                    component="div"
                    count={store.meta.total}
                    page={store.meta.page - 1}
                    onPageChange={(_, newPage) => store.setPage(newPage + 1)}
                    rowsPerPage={store.meta.per_page}
                    rowsPerPageOptions={[store.meta.per_page]}
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
                />
            </DialogActions>
        </Dialog>
    );
});

export default DomainManagerDialog;
